import {
	EventCreationModal,
	EventDetailPage,
	EventOccasionsListPage,
	EventOccasionOrderModal,
	useEventAdminManager,
	useEventDetail,
	useEventOccasionsList,
	useDeleteOccasion,
	type EventDetailData,
	type EventOccasion,
} from "@di-devs/event-feature-plugin/client";
import { BaseAnimationComponent } from "@/components";
import { useCallback, useState } from "react";
import { useLayoutConfigContext } from "@/providers/layoutConfig.provider";
import { PRIMARY_COLOR, PRIMARY_LIGHT_COLOR } from "@/utils/common";
import { message } from "antd";

const Events = () => {
	const { publicSettings } = useLayoutConfigContext();
	const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
	const [isEventModalOpen, setIsEventModalOpen] = useState(false);
	const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
	const [displayTab, setDisplayTab] = useState<"displayed" | "hidden">("displayed");
	const [editingEvent, setEditingEvent] = useState<EventDetailData | null>(null);

	const { handleUpdateOrder } = useEventAdminManager();

	const {
		occasions,
		loading,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
		refetch,
	} = useEventOccasionsList();

	const { data: eventDetail, isLoading: isDetailLoading, refetch: refetchEventDetail, error: eventDetailError } = useEventDetail(
		selectedEventId ?? undefined,
	);

	const deleteOccasionMutation = useDeleteOccasion();

	const handleEventClick = useCallback((occasion: EventOccasion) => {
		setSelectedEventId(occasion.eventOccasionId || occasion.id as number);
	}, []);

	const handleBackToList = useCallback(() => {
		setSelectedEventId(null);
		refetch();
	}, [refetch]);

	const handleEditEvent = useCallback(() => {
		if (eventDetail) {
			setEditingEvent(eventDetail);
			setIsEventModalOpen(true);
		}
	}, [eventDetail]);

	const handleDeleteEvent = useCallback(async () => {
		if (!selectedEventId) return;
		
		try {
			await deleteOccasionMutation.mutateAsync(selectedEventId);
			message.success("イベントを削除しました");
			setSelectedEventId(null);
			refetch();
		} catch (error: unknown) {
			console.error("Delete event error:", error);
			const errorMessage = error instanceof Error ? error.message : "削除に失敗しました";
			message.error(errorMessage);
			setSelectedEventId(null);
			refetch();
		}
	}, [selectedEventId, deleteOccasionMutation, refetch]);

	const primaryColor =
		publicSettings?.PRIMARY_COLOR?.valueString || PRIMARY_COLOR;
	const primaryLightColor =
		publicSettings?.PRIMARY_LIGHT_COLOR?.valueString || PRIMARY_LIGHT_COLOR;

	if (eventDetailError) {
		return (
			<BaseAnimationComponent>
				<div style={{ padding: "24px", textAlign: "center" }}>
					<p>イベントが見つかりませんでした。</p>
					<button
						type="button"
						onClick={handleBackToList}
						style={{
							marginTop: "16px",
							padding: "8px 16px",
							backgroundColor: primaryColor,
							color: "white",
							border: "none",
							borderRadius: "4px",
							cursor: "pointer",
						}}
					>
						一覧に戻る
					</button>
				</div>
			</BaseAnimationComponent>
		);
	}

	if (selectedEventId && eventDetail) {
		return (
			<BaseAnimationComponent>
				<EventDetailPage
					eventData={{
						eventOccasionId: eventDetail.eventOccasionId,
						title: eventDetail.title,
						subtitle: eventDetail.subtitle,
						description: eventDetail.description,
						heroImageUrl: eventDetail.heroImageUrl,
						gallery: eventDetail.gallery || [],
						isPublished: eventDetail.isPublished,
						reservationCount: eventDetail.reservationCount || 0,
						attendeeCount: eventDetail.attendeeCount || 0,
						maximumAttendees: eventDetail.maximumAttendees || 0,
						occurrences: eventDetail.occurrences || [],
						occasionImages: eventDetail.occasionImages,
						fee: eventDetail.fee,
						membershipScope: eventDetail.membershipScope,
					}}
					loading={isDetailLoading}
					primaryColor={primaryColor}
					primaryLightColor={primaryLightColor}
					imageBaseUrl=""
					systemType="イベント"
					onBack={handleBackToList}
					onEditEvent={handleEditEvent}
					onDeleteEvent={handleDeleteEvent}
				/>
				<EventCreationModal
					open={isEventModalOpen}
					onClose={() => {
						setIsEventModalOpen(false);
						setEditingEvent(null);
						refetch();
						if (selectedEventId) {
							refetchEventDetail();
						}
					}}
					currentEvent={editingEvent}
					systemType="イベント"
					primaryColor={primaryColor}
					isPaymentAvailable={true}
                    showCheckin={false}
                    showUniqueMessage={true}
                    showCancellationSettings={true}
                    showProgramSettings={false}
				/>
			</BaseAnimationComponent>
		);
	}

	return (
		<BaseAnimationComponent>
			<EventOccasionsListPage
				occasions={occasions}
				loading={loading || isFetchingNextPage}
				hasMore={hasNextPage}
				loadMore={() => {
					if (!isFetchingNextPage && hasNextPage) {
						fetchNextPage();
					}
				}}
				primaryColor={primaryColor}
				primaryLightColor={primaryLightColor}
				imageBaseUrl=""
				displayTab={displayTab}
				onTabChange={setDisplayTab}
				onEventClick={handleEventClick}
				onCreateEvent={() => setIsEventModalOpen(true)}
				onRegisterEvent={() => setIsEventModalOpen(true)}
				onEditDisplayOrder={() => setIsOrderModalOpen(true)}
			/>
			<EventOccasionOrderModal
				isOpen={isOrderModalOpen}
				onClose={() => setIsOrderModalOpen(false)}
				title={displayTab === "displayed" ? "表示イベント並べ替え" : "非表示イベント並べ替え"}
				eventOccasions={occasions.filter(o => 
					displayTab === "displayed" ? o.isDisplayed : !o.isDisplayed
				)}
				onUpdateOrder={handleUpdateOrder}
				primaryColor={primaryColor}
				primaryLightColor={primaryLightColor}
			/>
			<EventCreationModal
				open={isEventModalOpen}
				onClose={() => {
					setIsEventModalOpen(false);
					setEditingEvent(null);
					refetch();
					if (selectedEventId) {
						refetchEventDetail();
					}
				}}
				currentEvent={editingEvent}
				systemType="イベント"
				primaryColor={primaryColor}
				isPaymentAvailable={true}
                showCheckin={false}
                showUniqueMessage={true}
                showCancellationSettings={true}
                showProgramSettings={false}
			/>
		</BaseAnimationComponent>
	);
};

export default Events;
